import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import useAutoHeightAdjustment from '../../hooks/useAutoHeightAdjustment';
import { 
  FaHome, 
  FaBolt, 
  FaCreditCard, 
  FaEye, 
  FaTools, 
  FaChartLine, 
  FaShieldAlt,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaCheckCircle,
  FaUsers,
  FaClipboardCheck,
  FaHandshake,
  FaArrowLeft,
  FaHardHat,
  FaImages,
  FaPaintRoller,
  FaWrench,
  FaBars,
  FaTimes,
  FaWindowMaximize
} from 'react-icons/fa';
import ContactModal from '../ui/ContactModal';
import ReusableDropdown from '../ui/ReusableDropdown.jsx';
import { Footer } from '../index';
import './GeneralContractorPage.css';
import '../layout/PageMobileNav.css';

const GeneralContractorPage = () => {
  const navigate = useNavigate();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const titleRef = useScrollAnimation();
  const servicesRef = useScrollAnimation();
  const whoWeServeRef = useScrollAnimation();
  const whyTrustUsRef = useScrollAnimation();
  const ctaRef = useScrollAnimation();

  // Use the auto height adjustment hook for service cards
  const { sameHeightRef: servicesHeightRef, refresh: refreshServicesHeights } = useAutoHeightAdjustment({
    containerSelector: '.gc-services-grid.same-height',
    elementSelector: '[data-same-height]',
    debounceDelay: 200,
    observeChildList: true,
    observeAttributes: true,
    observeCharacterData: true,
    subtree: true
  });

  const handleContactClick = (e) => {
    e.preventDefault();
    window.location.href = 'tel:+13136316020';
  };

  // Navigation services for dropdown
  const navigationServices = [
    { id: 'roofing', text: 'Roofing', to: '/roofing', icon: FaHome },
    { id: 'windows', text: 'Windows', to: '/windows', icon: FaWindowMaximize },
    { id: 'siding', text: 'Siding', to: '/siding', icon: FaPaintRoller },
    { id: 'gutters', text: 'Gutters', to: '/gutters', icon: FaTools },
    { id: 'repairs', text: 'Repairs', to: '/repairs', icon: FaWrench },
    { id: 'all-services', text: 'All Services', to: '/all-services', icon: FaHardHat }
  ];

  // Mobile menu handlers
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(prev => !prev);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const openContactModal = () => {
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  const scrollToGCServices = (e) => {
    if (e) {
      e.preventDefault();
    }
    const gcServicesSection = document.getElementById('gc-services');
    if (gcServicesSection) {
      const yOffset = -80; // Adjust for header height if needed
      const y = gcServicesSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const gcServices = [
    {
      icon: FaHome,
      title: "Full-Scope General Contracting",
      description: "Let our licensed team manage the entire build or renovation. We handle scheduling, permits, procurement, quality control, and subcontractor management — so you can focus on the bigger picture.",
      features: [
        "Complete project management",
        "Permit handling & compliance",
        "Subcontractor coordination",
        "Quality control oversight",
        "Budget management"
      ]
    },
    {
      icon: FaEye,
      title: "Crew Oversight & Project Auditing",
      description: "Already have a team in place? We'll audit your contractor's performance, review budgets, inspect progress, and provide unbiased feedback. Think of us as your independent construction watchdog ensuring your money is well-spent.",
      features: [
        "Independent contractor auditing",
        "Budget review & analysis",
        "Progress inspection",
        "Performance evaluation",
        "Unbiased feedback & recommendations"
      ]
    },
    {
      icon: FaTools,
      title: "Targeted Trade Support",
      description: "Need only specific trades — roofing, HVAC, electrical, plumbing, or finish carpentry? We can plug in just where you need us, supplementing your existing crews to fill skill gaps and keep projects moving.",
      features: [
        "Specialized trade services",
        "Skill gap filling",
        "Project momentum maintenance",
        "Quality trade coordination",
        "Flexible service integration"
      ]
    },
    {
      icon: FaChartLine,
      title: "Progress Reports & Accountability",
      description: "We use tools like Airtable, Buildium, and CompanyCam to give investors live visibility into their projects. Expect regular photo updates, milestone reports, and transparent communication from start to finish.",
      features: [
        "Live project visibility",
        "Regular photo updates",
        "Milestone reporting",
        "Transparent communication",
        "Digital project tracking"
      ]
    }
  ];

  const whoWeServe = [
    {
      icon: FaUsers,
      title: "Real Estate Investors",
      description: "Local and remote investors needing trustworthy oversight.",
      image: "/images/general-contractor/contractor4.webp"
    },
    {
      icon: FaClipboardCheck,
      title: "Developers",
      description: "Multi-project developers requiring professional coordination.",
      image: "/images/general-contractor/contractor5.webp"
    },
    {
      icon: FaHome,
      title: "Property Owners",
      description: "Homeowners seeking unified project management.",
      image: "/images/general-contractor/contactor1.webp"
    }
  ];

  const trustFactors = [
    {
      icon: FaShieldAlt,
      title: "Licensed & Insured",
      description: "Fully licensed and insured for your protection."
    },
    {
      icon: FaCheckCircle,
      title: "400+ Projects Completed",
      description: "Proven track record across Detroit metro."
    },
    {
      icon: FaHandshake,
      title: "Transparent Communication",
      description: "Clear updates and no surprises."
    }
  ];

  return (
    <main className="general-contractor-page">
      <Helmet>
        <title>RC Roofing - General Contractor Services</title>
        <meta name="description" content="Professional general contracting and project oversight services for investors, developers, and property managers. Licensed & insured in Michigan with proven track record of 250+ managed homes." />
        <meta name="keywords" content="general contractor, project oversight, investor services, construction management, Michigan contractor, Detroit contractor, property management, construction auditing" />
        <link rel="canonical" href="https://rcroofing.com/gc-services" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://rcroofing.com/gc-services" />
        <meta property="og:title" content="General Contractor Services | RC Roofing | Investor Project Oversight" />
        <meta property="og:description" content="Professional general contracting and project oversight services for investors, developers, and property managers. Licensed & insured in Michigan with 250+ managed homes." />
        <meta property="og:image" content="https://rcroofing.com/images/services/Full Gut Rehab/full-gut-rehab1.webp" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="General Contractor Services | RC Roofing | Investor Project Oversight" />
        <meta name="twitter:description" content="Professional general contracting and project oversight services for investors, developers, and property managers." />
        <meta name="twitter:image" content="https://rcroofing.com/images/services/Full Gut Rehab/full-gut-rehab1.webp" />
      </Helmet>
      
      <div className="page-header">
        <div className="container">
          <div className="page-nav">
            <Link to="/" className="back-btn">
              <FaArrowLeft className="back-icon" />
              Back to Home
            </Link>
            {/* Desktop Navigation */}
            <div className="page-nav-right desktop-nav">
              <button 
                className="nav-item" 
                onClick={handleContactClick} 
                title="Call us"
              >
                <FaPhone className="nav-icon" />
                Call us
              </button>
              
              <ReusableDropdown
                triggerText="Services"
                items={navigationServices}
                className="gc-services-dropdown"
                triggerClassName="gc-nav-item"
                iconComponent={FaHardHat}
                showIcons={true}
              />
              
              <button 
                className="nav-item active" 
                onClick={scrollToGCServices} 
                title="General Contractor"
              >
                <FaUsers className="nav-icon" />
                General Contractor
              </button>
              <Link 
                to="/gallery" 
                className="nav-item" 
                title="Our Gallery"
              >
                <FaImages className="nav-icon" />
                Our Gallery
              </Link>
            </div>

            {/* Mobile Navigation */}
            <div className="mobile-nav">
              <button 
                className="hamburger-btn"
                onClick={toggleMobileMenu}
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="page-mobile-menu">
          <div className="page-mobile-menu-header">
            <div className="page-mobile-menu-title">Navigation</div>
            <button 
              className="page-mobile-close-btn"
              onClick={closeMobileMenu}
              aria-label="Close menu"
            >
              <FaTimes />
            </button>
          </div>
          
          <div className="page-mobile-menu-content">
            <button 
              className="page-mobile-nav-item"
              onClick={(e) => {
                handleContactClick(e);
                closeMobileMenu();
              }}
            >
              <FaPhone className="nav-icon" />
              Call us
            </button>
            
            <ReusableDropdown
              triggerText="Services"
              items={navigationServices}
              className="page-mobile-dropdown mobile-nav"
              iconComponent={FaHardHat}
              showIcons={true}
              onItemClick={closeMobileMenu}
            />
            
            <button 
              className="page-mobile-nav-item active"
              onClick={() => {
                scrollToGCServices();
                closeMobileMenu();
              }}
            >
              <FaUsers className="nav-icon" />
              General Contractor
            </button>
            
            <Link 
              to="/gallery"
              className="page-mobile-nav-item"
              onClick={closeMobileMenu}
            >
              <FaImages className="nav-icon" />
              Our Gallery
            </Link>
          </div>
        </div>
      )}
      
      <div className="page-header">
        <div className="container">
          <h1 className="page-title">General Contractor Services</h1>
          <p className="page-subtitle">
            Professional project oversight and general contracting for investors, developers, and property managers
          </p>
        </div>
      </div>
      
      {/* Services Grid - Combined Why Hire Us & What We Offer */}
      <section id="gc-services" className="gc-services" ref={servicesRef}>
        <div className="gc-container">
          <div className="gc-section-header gc-section-header-enhanced">
            <span className="gc-header-label">Your Trusted Partner</span>
            <h2>Expert General Contracting & Project Oversight</h2>
            <p>
              Professional construction management designed to protect your investment and ensure accountability. 
              Our experienced team handles everything from coordination to quality control, keeping your project 
              on schedule and within budget.
            </p>
          </div>
          
          <div className="gc-services-grid" ref={servicesHeightRef}>
            {gcServices.map((service, index) => (
              <div key={index} className="gc-service-card" data-same-height>
                <div className="gc-service-icon">
                  <service.icon />
                </div>
                <h3 className="gc-service-title">{service.title}</h3>
                <p className="gc-service-description">{service.description}</p>
                <ul className="gc-service-features">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex}>
                      <FaCheckCircle className="gc-feature-check" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="gc-hero">
        <div className="gc-hero-content">
          <div className="gc-hero-text">
            <h1 ref={titleRef} className="gc-hero-title">
              Investor Project Oversight & General Contracting Services
            </h1>
            <p className="gc-hero-subtitle">
              Your Trusted Partner for Project Management and Oversight
            </p>
            <p className="gc-hero-description">
              Whether you're an out-of-state investor, local developer, or property owner managing multiple renovations, 
              having the right general contractor can make or break your project's success. At RC Roofing, we go beyond 
              standard contracting — we act as your eyes, ears, and advocate on every job site.
            </p>
            <div className="gc-hero-buttons">
              <button className="gc-hero-button primary" onClick={openContactModal}>
                Request Oversight Services
              </button>
              <button className="gc-hero-button secondary" onClick={() => navigate('/all-services')}>
                View All Services
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve & Why Clients Trust Us - Combined Section */}
      <section className="gc-who-serve-trust" ref={whoWeServeRef}>
        <div className="gc-container">
          {/* Combined Header */}
          <div className="gc-section-header gc-section-header-enhanced">
            <h2>Trusted by Professionals, Built on Results</h2>
            <p>We partner with investors, developers, and property owners who demand excellence</p>
          </div>
          
          {/* Who We Serve - Image Cards Grid */}
          <div className="gc-serve-grid-with-images">
            {whoWeServe.map((client, index) => (
              <div key={index} className="gc-serve-card gc-serve-card-with-image">
                <img 
                  src={client.image} 
                  alt={client.title}
                  className="gc-serve-card-image"
                  loading="lazy"
                />
                <div className="gc-serve-card-content">
                  <h3 className="gc-serve-title">{client.title}</h3>
                  <p className="gc-serve-description">{client.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Why They Trust Us - Icon Cards Grid */}
          <div className="gc-trust-grid-with-icons">
            {trustFactors.map((factor, index) => (
              <div key={`trust-${index}`} className="gc-serve-card gc-serve-card-icon-only">
                <div className="gc-serve-card-content">
                  <div className="gc-serve-icon">
                    <factor.icon />
                  </div>
                  <h3 className="gc-serve-title">{factor.title}</h3>
                  <p className="gc-serve-description">{factor.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="gc-cta" ref={ctaRef}>
        <div className="gc-container">
          <div className="gc-cta-content">
            <h2>Let's Protect Your Investment</h2>
            <p>
              View us as your unbiased partner on the ground — ensuring every dollar, deadline, 
              and detail is handled with professionalism and integrity.
            </p>
            <button className="gc-cta-button" onClick={openContactModal}>
              Request Oversight Services Now
            </button>
          </div>
        </div>
      </section>
      
      <Footer />

      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={closeContactModal}
        formTitle="Let's Partner Together"
        formSubtitle="Ready to collaborate on your next project? Fill out the form below and let's discuss how we can help oversee your construction needs."
      />
    </main>
  );
};

export default GeneralContractorPage;
